import {
  FC,
  useEffect,
  useRef,
  createContext,
  useContext,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { BoxV2 as Box } from 'portal-commons';

interface MenuContextType {
  onItemClick: (value: string, event: ReactMouseEvent) => void;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

interface MenuProps extends React.ComponentProps<typeof Box> {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onItemClick?: (value: string, event: ReactMouseEvent) => void;
  minWidth?: number;
}
type MenuComponent = FC<MenuProps> & {
  MenuItem: typeof MenuItem;
};

const Menu: MenuComponent = ({
  open,
  anchorEl,
  onClose,
  onItemClick,
  children,
  sx,
  ...restProps
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // must return something to avoid the bottom return violates consistent-return lint rule
    if (!open) return undefined;

    const bodyStyles = {
      paddingRight: document.body.style.paddingRight,
      overflow: document.body.style.overflow,
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Calculate and apply scrollbar compensation
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollBarWidth}px`;
    document.body.style.overflow = 'hidden';

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // Restore original body styles
      document.body.style.paddingRight = bodyStyles.paddingRight;
      document.body.style.overflow = bodyStyles.overflow;
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  const handleItemClick = (value: string, event: ReactMouseEvent) => {
    onItemClick?.(value, event);
    onClose();
  };

  if (!open || !anchorEl) return null;

  const rect = anchorEl.getBoundingClientRect();
  const minWidth =
    restProps.minWidth ||
    parseInt((sx as Record<string, any>)?.minWidth, 10) ||
    180;

  return createPortal(
    <MenuContext.Provider value={{ onItemClick: handleItemClick }}>
      <Box
        as="ul"
        ref={menuRef}
        role="menu"
        sx={{
          position: 'fixed',
          left: `${rect.left - minWidth}px`,
          top: `${rect.top}px`,
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
          padding: '8px 0',
          zIndex: 99,
          listStyle: 'none',
          margin: 0,
          ...sx,
          minWidth,
        }}
        {...restProps}
      >
        {children}
      </Box>
    </MenuContext.Provider>,
    document.body
  );
};

// MenuItem component
interface MenuItemProps extends React.ComponentProps<typeof Box> {
  value: string;
  disabled?: boolean;
}

const MenuItem: FC<MenuItemProps> = ({
  value,
  disabled = false,
  children,
  sx,
  ...restProps
}) => {
  const menuContext = useContext(MenuContext);

  if (!menuContext) {
    throw new Error('MenuItem must be used within a Menu component');
  }

  const handleClick = (event: ReactMouseEvent) => {
    event.stopPropagation();
    if (!disabled) {
      menuContext.onItemClick(value, event);
    }
  };

  return (
    <Box
      as="li"
      role="menuitem"
      aria-disabled={disabled}
      onClick={handleClick}
      sx={{
        backgroundColor: 'white',
        color: disabled ? '#A1A1A1' : 't.black80',
        padding: '8px 16px',
        borderRadius: '4px',
        marginBottom: '4px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 'H200',
        textAlign: 'left',
        '&:last-child': {
          marginBottom: 0,
        },
        '&:hover': {
          backgroundColor: disabled ? undefined : '#F2F3F3',
        },
        ...sx,
      }}
      {...restProps}
    >
      {children}
    </Box>
  );
};

Menu.MenuItem = MenuItem;

export default Menu;
